import { nanoid } from 'nanoid';
import { WorkoutProgram } from '../types/workouts';

export const workoutPrograms: WorkoutProgram[] = [
  {
    id: nanoid(),
    name: "Elite Performance Series",
    description: "A comprehensive 12-week program designed to transform your fitness level through progressive overload and periodization.",
    duration: "12 weeks",
    level: "Elite",
    category: "Strength",
    image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80",
    coach: {
      name: "Chris Glory",
      image: "https://images.unsplash.com/photo-1563351672-62b74891a28a?w=400&h=400&fit=crop&q=80",
      specialties: ["Elite Performance", "Strength Training", "Athletic Development"]
    },
    schedule: {
      weeks: 12,
      workoutsPerWeek: 5
    },
    goals: [
      "Build lean muscle mass",
      "Increase strength and power",
      "Improve athletic performance",
      "Enhance metabolic conditioning"
    ],
    equipment: [
      "Full gym access",
      "Barbell and plates",
      "Dumbbells",
      "Kettlebells",
      "Resistance bands"
    ],
    workouts: [
      {
        id: nanoid(),
        name: "Power Development",
        type: "Strength",
        duration: 75,
        intensity: "High",
        targetStrain: 16.5,
        description: "Focus on explosive movements and heavy compound lifts",
        exercises: [
          {
            name: "Dynamic Warm-Up",
            duration: 600,
            intensity: "Light",
            description: "Mobility and activation drills"
          },
          {
            name: "Power Complex",
            duration: 1800,
            intensity: "High",
            description: "Clean pulls, box jumps, medicine ball throws"
          },
          {
            name: "Strength Work",
            duration: 1800,
            intensity: "High",
            description: "Squats, deadlifts, bench press"
          },
          {
            name: "Cool Down",
            duration: 300,
            intensity: "Light",
            description: "Static stretching and mobility work"
          }
        ]
      }
    ],
    features: [
      "Personalized progression tracking",
      "Video technique guides",
      "Nutrition recommendations",
      "Recovery protocols",
      "Weekly progress check-ins"
    ]
  },
  {
    id: nanoid(),
    name: "Running Studio",
    description: "Whether you're training for a marathon or just looking to add spring to your step, our Running Studio is the ultimate place to move.",
    duration: "Ongoing",
    level: "All Levels",
    category: "Cardio",
    image: "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=800&q=80",
    coach: {
      name: "Emma Thompson",
      image: "https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=400&h=400&fit=crop&q=80",
      specialties: ["Running", "Endurance", "HIIT"]
    },
    schedule: {
      weeks: 0,
      workoutsPerWeek: 3
    },
    goals: [
      "Improve running form",
      "Build endurance",
      "Increase speed",
      "Enhance recovery"
    ],
    equipment: [
      "Treadmill",
      "Free weights",
      "Resistance bands",
      "Foam roller"
    ],
    workouts: [
      {
        id: nanoid(),
        name: "Sprint",
        type: "Speed",
        duration: 45,
        intensity: "High",
        targetStrain: 16.0,
        description: "Tackle short intervals at high intensities to push your acceleration, power, and speed to the max.",
        exercises: [
          {
            name: "Warm-Up",
            duration: 600,
            intensity: "Light",
            description: "Dynamic stretching and mobility"
          },
          {
            name: "Sprint Intervals",
            duration: 1500,
            intensity: "High",
            description: "High-intensity sprint intervals"
          },
          {
            name: "Recovery Intervals",
            duration: 300,
            intensity: "Low",
            description: "Active recovery between sprints"
          },
          {
            name: "Cool Down",
            duration: 300,
            intensity: "Light",
            description: "Gradual pace reduction and stretching"
          }
        ]
      },
      {
        id: nanoid(),
        name: "Capacity",
        type: "Endurance",
        duration: 50,
        intensity: "Moderate-High",
        targetStrain: 14.0,
        description: "Build your capacity to maintain high intensities for extended periods with challenging intervals.",
        exercises: [
          {
            name: "Build-Up",
            duration: 600,
            intensity: "Progressive",
            description: "Gradually increasing pace"
          },
          {
            name: "Threshold Work",
            duration: 1800,
            intensity: "High",
            description: "Sustained effort at threshold pace"
          },
          {
            name: "Recovery Blocks",
            duration: 600,
            intensity: "Moderate",
            description: "Active recovery periods"
          }
        ]
      },
      {
        id: nanoid(),
        name: "Endure",
        type: "Long Distance",
        duration: 60,
        intensity: "Moderate",
        targetStrain: 13.0,
        description: "Focus on distance and endurance with steady-state running and progressive intervals.",
        exercises: [
          {
            name: "Warm-Up",
            duration: 600,
            intensity: "Light",
            description: "Easy pace build-up"
          },
          {
            name: "Distance Block",
            duration: 2400,
            intensity: "Moderate",
            description: "Sustained endurance running"
          },
          {
            name: "Finish Strong",
            duration: 600,
            intensity: "Moderate-High",
            description: "Progressive pace increase"
          }
        ]
      }
    ],
    features: [
      "Form analysis",
      "Pace tracking",
      "Heart rate zones",
      "Recovery metrics",
      "Group challenges"
    ]
  },
  {
    id: nanoid(),
    name: "Strength Studio",
    description: "Realize your true strength alongside a supportive and ambitious community. Our expert trainers guide you through proven lifting techniques with real-time feedback.",
    duration: "Ongoing",
    level: "All Levels",
    category: "Strength",
    image: "https://images.unsplash.com/photo-1534368420009-621bfab424a8?w=800&q=80",
    coach: {
      name: "Marcus Chen",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&q=80",
      specialties: ["Strength Training", "Olympic Lifting", "Power Development"]
    },
    schedule: {
      weeks: 0,
      workoutsPerWeek: 3
    },
    goals: [
      "Build raw strength",
      "Improve technique",
      "Increase power",
      "Develop muscle mass"
    ],
    equipment: [
      "Barbells",
      "Power racks",
      "Free weights",
      "Platforms"
    ],
    workouts: [
      {
        id: nanoid(),
        name: "Forge",
        type: "Foundational Strength",
        duration: 60,
        intensity: "High",
        targetStrain: 15.5,
        description: "Focus on high-load, low-volume sets to build raw strength and power.",
        exercises: [
          {
            name: "Technique Prep",
            duration: 600,
            intensity: "Light",
            description: "Movement patterns and activation"
          },
          {
            name: "Main Lifts",
            duration: 2400,
            intensity: "High",
            description: "Heavy compound movements"
          },
          {
            name: "Accessory Work",
            duration: 600,
            intensity: "Moderate",
            description: "Supporting exercises"
          }
        ]
      },
      {
        id: nanoid(),
        name: "Build",
        type: "Hypertrophy",
        duration: 55,
        intensity: "Moderate-High",
        targetStrain: 13.5,
        description: "Focus on muscle growth with higher volume and moderate loads.",
        exercises: [
          {
            name: "Activation",
            duration: 600,
            intensity: "Light",
            description: "Muscle activation and mobility"
          },
          {
            name: "Volume Work",
            duration: 2100,
            intensity: "Moderate-High",
            description: "Multiple sets with moderate loads"
          },
          {
            name: "Finisher",
            duration: 600,
            intensity: "High",
            description: "High-volume burnout sets"
          }
        ]
      },
      {
        id: nanoid(),
        name: "Sustain",
        type: "Strength Endurance",
        duration: 50,
        intensity: "Moderate",
        targetStrain: 12.0,
        description: "Build muscular endurance with higher reps and shorter rest periods.",
        exercises: [
          {
            name: "Movement Prep",
            duration: 600,
            intensity: "Light",
            description: "Dynamic warm-up"
          },
          {
            name: "Circuit Work",
            duration: 1800,
            intensity: "Moderate",
            description: "Strength endurance circuits"
          },
          {
            name: "Core & Stability",
            duration: 600,
            intensity: "Moderate",
            description: "Core strengthening"
          }
        ]
      }
    ],
    features: [
      "Form analysis",
      "Progress tracking",
      "1RM calculations",
      "Volume monitoring",
      "Technique feedback"
    ]
  },
  {
    id: nanoid(),
    name: "Movement Studio",
    description: "Discover the power of functional movement patterns and dynamic exercises that enhance your everyday performance.",
    duration: "Ongoing",
    level: "All Levels",
    category: "Mixed",
    image: "https://images.unsplash.com/photo-1517838277536-f5f99be501cd?w=800&q=80",
    coach: {
      name: "Sophia Rodriguez",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&q=80",
      specialties: ["Movement", "Mobility", "Functional Training"]
    },
    schedule: {
      weeks: 0,
      workoutsPerWeek: 2
    },
    goals: [
      "Improve mobility",
      "Enhance movement patterns",
      "Build functional strength",
      "Increase body control"
    ],
    equipment: [
      "Bodyweight",
      "Resistance bands",
      "Light weights",
      "Mobility tools"
    ],
    workouts: [
      {
        id: nanoid(),
        name: "Flow",
        type: "Movement",
        duration: 45,
        intensity: "Moderate",
        targetStrain: 10.0,
        description: "Dynamic movement patterns and flow sequences.",
        exercises: [
          {
            name: "Movement Prep",
            duration: 600,
            intensity: "Light",
            description: "Joint mobility and activation"
          },
          {
            name: "Flow Sequences",
            duration: 1500,
            intensity: "Moderate",
            description: "Connected movement patterns"
          },
          {
            name: "Skill Work",
            duration: 600,
            intensity: "Moderate",
            description: "Movement skill development"
          }
        ]
      }
    ],
    features: [
      "Movement assessment",
      "Mobility tracking",
      "Pattern progression",
      "Flow development",
      "Recovery guidance"
    ]
  },
  {
    id: nanoid(),
    name: "Sunday Sweat",
    description: "Switch it up every Sunday with a dynamic blend of endurance runs, explosive sprints, and cross-training that builds strength and stamina.",
    duration: "Ongoing",
    level: "All Levels",
    category: "Mixed",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=80",
    coach: {
      name: "David Kim",
      image: "https://images.unsplash.com/photo-1600486913747-55e5470d6f40?w=400&h=400&fit=crop&q=80",
      specialties: ["Cross Training", "HIIT", "Group Fitness"]
    },
    schedule: {
      weeks: 0,
      workoutsPerWeek: 1
    },
    goals: [
      "Build overall fitness",
      "Improve conditioning",
      "Enhance endurance",
      "Boost energy levels"
    ],
    equipment: [
      "Mixed equipment",
      "Cardio machines",
      "Free weights",
      "Bodyweight"
    ],
    workouts: [
      {
        id: nanoid(),
        name: "Sunday Sweat Session",
        type: "Mixed",
        duration: 60,
        intensity: "High",
        targetStrain: 15.0,
        description: "A challenging mix of cardio and strength work that changes every week.",
        exercises: [
          {
            name: "Dynamic Warm-Up",
            duration: 600,
            intensity: "Light",
            description: "Full body activation"
          },
          {
            name: "Circuit Work",
            duration: 2400,
            intensity: "High",
            description: "Mixed cardio and strength circuits"
          },
          {
            name: "Team Challenge",
            duration: 600,
            intensity: "High",
            description: "Group-based workout finale"
          }
        ]
      }
    ],
    features: [
      "Weekly challenges",
      "Community events",
      "Performance tracking",
      "Social workouts",
      "Variable programming"
    ]
  }
];